import * as React from 'react';
import { X } from 'lucide-react';

import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FloatingNotificationCardProps {
  /** Inline text-link actions rendered below the description (not a footer button row). */
  actions?: React.ReactNode;
  /** Body content. Prefer `description` for plain text/nodes; `children` is an escape hatch for richer content. */
  children?: React.ReactNode;
  /** Optional CSS class(es) merged onto the outer fixed wrapper via cn() — lets callers avoid slot collisions. */
  className?: string;
  /** aria-label for the close button — no i18n in the lib, all text arrives via props. */
  closeLabel: string;
  /** Body text below the title — ignored when `children` is provided. */
  description?: React.ReactNode;
  /** Pre-rendered, pre-styled icon node (caller owns size and color classes). */
  icon: React.ReactNode;
  /** Called when the close (X) button is clicked. */
  onClose: () => void;
  /** Title text. */
  title: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Presentational floating notification card, anchored to the top inline-end of the viewport
 * (right in LTR, left in RTL).
 *
 * - No i18n and no business logic — every label arrives via props.
 * - No auto-dismiss: the caller decides the card's visible lifecycle (e.g. a single-slot store
 *   that replaces the visible card when a newer notification arrives).
 * - `role="status"` + `aria-live="polite"` for accessibility; the close button carries
 *   `aria-label={closeLabel}`.
 */
export function FloatingNotificationCard({
  actions,
  children,
  className,
  closeLabel,
  description,
  icon,
  onClose,
  title,
}: FloatingNotificationCardProps): React.JSX.Element {
  return (
    <div
      aria-live="polite"
      className={cn(
        'fixed top-20 end-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] animate-in ltr:slide-in-from-right rtl:slide-in-from-left duration-300',
        className,
      )}
      role="status"
    >
      <div className="rounded-xl border bg-card p-4 shadow-lg">
        <div className="flex items-start gap-3">
          {icon}
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium text-foreground">{title}</p>
            {/* Separate elements: block `children` inside the same <p> as `description` would be invalid HTML. */}
            {children ? (
              <div className="text-sm text-muted-foreground">{children}</div>
            ) : (
              description && <p className="text-sm text-muted-foreground">{description}</p>
            )}
            {actions && <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">{actions}</div>}
          </div>
          <button
            aria-label={closeLabel}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            onClick={onClose}
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
